import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createPortal } from 'react-dom';
import ImageGallery from 'react-image-gallery';
import type { ImageGalleryRef, GalleryItem as ReactImageGalleryItem } from 'react-image-gallery';
import { TransformWrapper, TransformComponent } from 'react-zoom-pan-pinch';
import { config } from '@config/env';
import {
  updateFavoriteAttachment,
  downloadFile,
  downloadAllFiles,
} from '@services/imagesGalleryService';
import { apiService } from '@services/api';
import { conversationService } from '@services/conversationService';
import { ImageFeedbackEditor } from '@components/ImageFeedbackEditor';
import { SaveAnnotatedImageModal } from './SaveAnnotatedImageModal';
import { useToast } from '@components/ui/Toast';
import { conversationBus } from '@/lib/conversationBus';
import { getMaxZoomScale } from '@/lib/imageZoom';
import 'react-image-gallery/styles/image-gallery.css';
import './ImagesGallery.css';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface Attachment {
  AttachmentId: number;
  EmbeddedImageId?: number;
  FileUrl: string;
  Thumbnail: { FileUrl: string };
  Description?: string;
  Title?: string;
  FileName: string;
  FileExtension: string;
  Favorite?: number;
  // Conversation context (optional, used for default reply target)
  ConversationId?: number;
  ConversationTitle?: string;
  ProjectName?: string;
  ProjectId?: number;
}

export interface ImagesGalleryProps {
  open: boolean;
  currentImageId?: number;
  attachments?: Attachment[];
  index?: number;
  isEmbedded?: boolean;
  onClose?: () => void;
  onMarkImage?: (attachmentId: number, embeddedImageId: number, isFavorite: boolean) => void;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function ensureProtocol(url: string): string
{
  if (url && !url.startsWith('http://') && !url.startsWith('https://'))
  {
    return `http://${url}`;
  }
  return url;
}

/** Absolute link to the standalone image viewer page. */
function buildImageLink(fileName: string): string
{
  const base = ensureProtocol(config.web.url).replace(/\/+$/, '');
  return `${base}/Dashboard/FileImage/#/${fileName}`;
}

function base64ToFile(base64: string, filename: string): File
{
  // Remove data URL prefix if present (e.g., "data:image/jpeg;base64,")
  const base64Data = base64.includes(',') ? base64.split(',')[1]! : base64;

  // Detect MIME type from data URL or default to jpeg (editor exports as JPEG)
  const mimeMatch = base64.match(/data:([^;]+);/);
  const mimeType = mimeMatch ? mimeMatch[1] : 'image/jpeg';

  // Convert base64 to binary
  const binaryString = atob(base64Data);
  const bytes = new Uint8Array(binaryString.length);
  for (let i = 0; i < binaryString.length; i++)
  {
    bytes[i] = binaryString.charCodeAt(i);
  }

  // Create blob and then file
  const blob = new Blob([bytes], { type: mimeType });
  return new File([blob], filename, { type: mimeType });
}

function buildImageUrl(attachment: Attachment, isEmbedded?: boolean): string
{
  // Use direct URL for gallery display (faster)
  if (attachment.FileUrl) return attachment.FileUrl;
  const base = ensureProtocol(config.web.url);
  if (isEmbedded && attachment.EmbeddedImageId)
  {
    return `${base}/Attachment/GetEmbeddedImage?embeddedImageId=${attachment.EmbeddedImageId}`;
  }
  return `${base}/Attachment/GetAttachment?attachmentId=${attachment.AttachmentId}`;
}

function buildImageUrlForEdit(attachment: Attachment): string
{
  // Use endpoint that returns base64 directly for editing
  const base = config.api.baseUrl.replace(/\/$/, '');

  if (attachment.EmbeddedImageId)
  {
    return `${base}/api/EmbebbedImages/GetFileBase64?attachmentId=${attachment.EmbeddedImageId}`;
  }

  return `${base}/api/Attachment/GetFileBase64?attachmentId=${attachment.AttachmentId}`;
}

function buildThumbnailUrl(attachment: Attachment, isEmbedded?: boolean): string
{
  if (attachment.Thumbnail?.FileUrl) return attachment.Thumbnail.FileUrl;
  return buildImageUrl(attachment, isEmbedded);
}

// Baseline zoom ceiling; getMaxZoomScale raises it for images shown below natural size
const DEFAULT_MAX_ZOOM_SCALE = 4;

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const ImagesGallery: React.FC<ImagesGalleryProps> = ({
  open,
  currentImageId,
  attachments = [],
  index: initialIndex,
  isEmbedded,
  onClose,
  onMarkImage,
}) =>
{
  const toast = useToast();
  const [currentIndex, setCurrentIndex] = useState(0);
  // Per-image zoom ceiling, measured once each image has loaded
  const [maxZoomScales, setMaxZoomScales] = useState<Record<number, number>>({});
  const [isFavorite, setIsFavorite] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  const [contextMenu, setContextMenu] = useState<{ x: number; y: number } | null>(null);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editorHidden, setEditorHidden] = useState(false);
  const [editingImageUrl, setEditingImageUrl] = useState<string>('');
  const [saveModalOpen, setSaveModalOpen] = useState(false);
  const [annotatedImageBase64, setAnnotatedImageBase64] = useState<string>('');

  const galleryRef = useRef<ImageGalleryRef>(null);
  const transformRefs = useRef<Map<number, any>>(new Map());
  const linkCopiedTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Sync props to state
  useEffect(() =>
  {
    if (!open || attachments.length === 0) return;

    let idx = initialIndex ?? 0;
    if (currentImageId != null)
    {
      const found = attachments.findIndex(
        a => a.AttachmentId === currentImageId || a.EmbeddedImageId === currentImageId,
      );
      if (found >= 0) idx = found;
    }
    setCurrentIndex(idx);
    setIsFavorite(!!attachments[idx]?.Favorite);
  }, [open, currentImageId, attachments, initialIndex, isEmbedded]);

  const handleClose = useCallback(() =>
  {
    onClose?.();
  }, [onClose]);

  const handleSlide = useCallback(
    (index: number) =>
    {
      setCurrentIndex(index);
      setIsFavorite(!!attachments[index]?.Favorite);
    },
    [attachments],
  );

  const handleToggleFavorite = useCallback(
    async (e: React.MouseEvent) =>
    {
      e.stopPropagation(); // ImageGallery might catch clicks
      const attachment = attachments[currentIndex];
      if (!attachment) return;

      const newFav = !isFavorite;
      setIsFavorite(newFav);
      const embId = attachment.EmbeddedImageId ?? 0;

      try
      {
        await updateFavoriteAttachment(attachment.AttachmentId, embId, newFav);
        onMarkImage?.(attachment.AttachmentId, embId, newFav);
      }
      catch (err)
      {
        setIsFavorite(!newFav);
        console.error('❌ updateFavoriteAttachment failed:', err);
      }
    },
    [attachments, currentIndex, isFavorite, onMarkImage],
  );

  const copyLinkToClipboard = useCallback(
    (e?: React.MouseEvent) =>
    {
      e?.stopPropagation();
      const attachment = attachments[currentIndex];
      if (!attachment) return;

      const text = buildImageLink(attachment.FileName);
      navigator.clipboard
        .writeText(text)
        .then(() =>
        {
          setLinkCopied(true);
          if (linkCopiedTimer.current) clearTimeout(linkCopiedTimer.current);
          linkCopiedTimer.current = setTimeout(() => setLinkCopied(false), 1800);
        })
        .catch(() =>
        {
          console.error('❌ Clipboard write failed');
        });
    },
    [attachments, currentIndex],
  );

  const openInNewTab = useCallback(
    (e?: React.MouseEvent) =>
    {
      e?.stopPropagation();
      const attachment = attachments[currentIndex];
      if (!attachment) return;
      window.open(buildImageLink(attachment.FileName), '_blank');
    },
    [attachments, currentIndex],
  );

  const handleDownload = useCallback(
    async (e?: React.MouseEvent) =>
    {
      e?.stopPropagation();
      const attachment = attachments[currentIndex];
      if (!attachment) return;
      try
      {
        const fileName =
          (attachment.Description || attachment.FileName || 'download') +
          (attachment.FileExtension || '');
        await downloadFile(
          attachment.AttachmentId,
          attachment.EmbeddedImageId ?? 0,
          fileName,
          attachment.FileUrl,
        );
      }
      catch (err)
      {
        console.error('❌ downloadFile failed:', err);
      }
    },
    [attachments, currentIndex],
  );

  const handleDownloadAll = useCallback(
    async (e?: React.MouseEvent) =>
    {
      e?.stopPropagation();
      if (attachments.length === 0) return;
      try
      {
        await downloadAllFiles(attachments);
      }
      catch (err)
      {
        console.error('❌ downloadAllFiles failed:', err);
      }
    },
    [attachments],
  );

  const handleContextMenu = useCallback((e: React.MouseEvent) =>
  {
    e.preventDefault();
    setContextMenu({ x: e.clientX, y: e.clientY });
  }, []);

  const handleContextMenuOpen = useCallback(() =>
  {
    openInNewTab();
    setContextMenu(null);
  }, [openInNewTab]);

  const handleEditImage = useCallback(async (e?: React.MouseEvent) =>
  {
    e?.stopPropagation();
    const currentAttachment = attachments[currentIndex];
    if (!currentAttachment) return;

    const imageUrl = buildImageUrlForEdit(currentAttachment);

    try
    {
      const token = localStorage.getItem('C2_access_token') || localStorage.getItem('access_token');
      const headers: HeadersInit = {};

      if (token)
      {
        headers.Authorization = `Bearer ${token}`;
      }

      const response = await fetch(imageUrl, {
        credentials: 'include',
        mode: 'cors',
        headers,
      });

      if (!response.ok)
      {
        throw new Error(`HTTP ${response.status}`);
      }

      const data = await response.json();

      if (!data.dataUrl || data.dataUrl.length < 100)
      {
        console.error('[ImagesGallery] Invalid image data received');
        alert('Failed to load image. Please try again.');
        return;
      }

      setEditingImageUrl(data.dataUrl);
      setEditorOpen(true);
    }
    catch (err)
    {
      console.error('[ImagesGallery] ❌ Failed to load image for editing:', err);
      alert(
        'Cannot load image for editing.\n\n' +
        'Please make sure the backend CORS headers are configured correctly.',
      );
    }
  }, [attachments, currentIndex, isEmbedded]);

  const handleSaveAnnotatedImage = useCallback((base64: string | null) =>
  {
    if (!base64)
    {
      alert('Export failed. Please try again or use a different image format.');
      return;
    }

    setAnnotatedImageBase64(base64);
    setEditorHidden(true);
    setSaveModalOpen(true);
  }, []);

  const handleCancelEdit = useCallback(() =>
  {
    setEditorOpen(false);
    setEditorHidden(false);
    setAnnotatedImageBase64('');
  }, []);

  const handleDownloadAnnotatedImage = useCallback((base64ToDownload?: string) =>
  {
    const imageToDownload = base64ToDownload || annotatedImageBase64;
    if (!imageToDownload) return;

    // Convert base64 to blob
    const byteString = atob(imageToDownload.split(',')[1] ?? '');
    const mimeString = imageToDownload.split(',')[0]?.split(':')[1]?.split(';')[0] ?? 'image/png';
    const ab = new ArrayBuffer(byteString.length);
    const ia = new Uint8Array(ab);
    for (let i = 0; i < byteString.length; i++)
    {
      ia[i] = byteString.charCodeAt(i);
    }
    const blob = new Blob([ab], { type: mimeString });

    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `annotated-${Date.now()}.jpg`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);

    // Close editor if download was from editor
    if (base64ToDownload)
    {
      setEditorOpen(false);
      setEditorHidden(false);
      setAnnotatedImageBase64('');
    }
  }, [annotatedImageBase64]);

  const handleCreateReply = useCallback(async (
    cardId: number,
    layerId: number,
    layerName: string,
    layerColor: string,
    text: string,
    additionalImages?: File[],
    phaseId?: number,
    phaseName?: string,
  ) =>
  {
    try
    {
      const uploadedAttachments: any[] = [];

      if (annotatedImageBase64)
      {
        const annotatedFile = base64ToFile(annotatedImageBase64, `annotated-${Date.now()}.jpg`);
        const attachment = await conversationService.uploadAttachment(
          annotatedFile,
          cardId,
          undefined,
        );
        uploadedAttachments.push(attachment);
      }

      if (additionalImages && additionalImages.length > 0)
      {
        for (const file of additionalImages)
        {
          const attachment = await conversationService.uploadAttachment(
            file,
            cardId,
            undefined,
          );
          uploadedAttachments.push(attachment);
        }
      }

      const userName = localStorage.getItem('C2_user_name') || 'User';
      const userImage = localStorage.getItem('C2_user_image') || 'localhost/Corgee.WebClient/Images/user.png';

      const replyPayload = {
        ReplyId: 0,
        ConversationId: cardId,
        ReplyLayers: [
          {
            LayerId: layerId,
            LayerColor: layerColor,
            LayerName: layerName,
            ReplyId: 0,
          },
        ],
        Attachments: uploadedAttachments,
        Body: text,
        CopyBody: text,
        Tags: [],
        SocketConnectionId: '',
        type: 1,
        possibleTags: [],
        CreatedBy: userName,
        ImageName: userImage,
        reactSelfUpdate: false,
        TemporalReply: true,
        reactions: [],
        ConversationPhaseId: phaseId || 0,
        PhaseName: phaseName || 'All Phases',
      };

      const response = await apiService.post('api/reply', replyPayload);

      // Show success notification
      toast.success('Your dashboard has changed', 'Reply created successfully');

      // Emit event to update dashboard and InfoWindow
      conversationBus.emit('reply-added', { conversationId: cardId, reply: response });

      // Close all modals and gallery
      setSaveModalOpen(false);
      setEditorOpen(false);
      setEditorHidden(false);
      setAnnotatedImageBase64('');
      onClose?.(); // Close the entire gallery
    }
    catch (error)
    {
      console.error('[ImagesGallery] ❌ Error creating reply:', error);
      toast.error('Failed to create reply. Please check console for details.', 'Error');
    }
  }, [annotatedImageBase64, toast, onClose]);

  const handleCloseSaveModal = useCallback(() =>
  {
    setSaveModalOpen(false);
    setEditorOpen(false);
    setEditorHidden(false);
    setAnnotatedImageBase64('');
  }, []);

  const handleBackToEditor = useCallback(() =>
  {
    setSaveModalOpen(false);
    setEditorHidden(false);
  }, []);

  // Clean timer & Escape key
  useEffect(() =>
  {
    const handleKeyDown = (e: KeyboardEvent) =>
    {
      if (e.key === 'Escape' && open) handleClose();
    };
    const handleClickOutside = () => setContextMenu(null);
    document.addEventListener('keydown', handleKeyDown);
    document.addEventListener('click', handleClickOutside);
    return () =>
    {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('click', handleClickOutside);
      if (linkCopiedTimer.current) clearTimeout(linkCopiedTimer.current);
    };
  }, [open, handleClose]);

  // -------------------------------------------------------------------------
  // Renderers
  // -------------------------------------------------------------------------

  // Custom item renderer for ZOOM
  const renderItem = (item: ReactImageGalleryItem) =>
  {
    const itemIndex = galleryItems.findIndex(gi => gi.original === item.original);
    return (
      <div className="ig-zoom-wrapper" onContextMenu={handleContextMenu}>
        <TransformWrapper
          ref={el =>
          {
            if (el) transformRefs.current.set(itemIndex, el);
            else transformRefs.current.delete(itemIndex);
          }}
          initialScale={1}
          minScale={0.5}
          maxScale={maxZoomScales[itemIndex] ?? DEFAULT_MAX_ZOOM_SCALE}
          centerZoomedOut={false}
          limitToBounds={false}
          panning={{ velocityDisabled: true }}
        >
          {/* Favorite icon inside image area - top right */}
          <div
            className={`ig-favorite-overlay ${isFavorite ? 'active' : ''}`}
            title="Mark as Favorite"
            onClick={handleToggleFavorite}
          >
            <i className="fa fa-picture-o"></i>
          </div>

          {/* Center Image button - scoped to this image's own viewing area */}
          <div
            className="ig-reset-cont"
            onClick={e =>
            {
              e.stopPropagation();
              transformRefs.current.get(itemIndex)?.resetTransform();
            }}
          >
            <span role="button" tabIndex={0} className="ig-reset-img">
              <i className="material-icons">filter_center_focus</i>
            </span>
          </div>

          <TransformComponent
            wrapperClass="ig-transform-component"
            contentClass="ig-transform-content"
            wrapperStyle={{ width: '100%', height: '100%' }}
          >
            <img
              src={item.original}
              alt={item.description}
              className="image-gallery-image"
              style={{ maxHeight: '50vh', maxWidth: '100%', objectFit: 'contain' }}
              onLoad={e =>
              {
                const maxScale = getMaxZoomScale(e.currentTarget, DEFAULT_MAX_ZOOM_SCALE);
                setMaxZoomScales(prev => (prev[itemIndex] === maxScale ? prev : { ...prev, [itemIndex]: maxScale }));
              }}
              onContextMenu={e => e.preventDefault()}
              draggable={false}
            />
          </TransformComponent>
        </TransformWrapper>
      </div>
    );
  };

  // Convert attachments to gallery items
  const galleryItems = attachments.map(att => ({
    original: buildImageUrl(att, isEmbedded),
    thumbnail: buildThumbnailUrl(att, isEmbedded),
    description: att.FileName,
    originalAlt: att.FileName,
    thumbnailAlt: att.FileName,
  }));

  // Custom Controls (Header) - edit, open new tab, copy link, close
  const renderCustomControls = () =>
  {
    return (
      <div className="ig-header-controls">
        {/* Edit/Annotate */}
        <div className="ig-icon-btn" title="Edit / Annotate" onClick={handleEditImage}>
          <i className="material-icons">edit</i>
        </div>

        {/* Open New Tab */}
        <div className="ig-icon-btn" title="Open New Tab" onClick={openInNewTab}>
          <i className="material-icons">open_in_new</i>
        </div>

        {/* Copy Link */}
        <div className="ig-icon-btn" title="Copy Link" onClick={copyLinkToClipboard}>
          <i className="material-icons">link</i>
        </div>

        {/* Close */}
        <button type="button" className="ig-close-btn" onClick={handleClose}>
          <i className="material-icons">close</i>
        </button>
      </div>
    );
  };

  if (!open || attachments.length === 0) return null;

  return (
    <>
      {/* Only show gallery when editor is not open */}
      {!editorOpen && createPortal(
        <div className="ig-overlay" onClick={handleClose}>
          <div className="ig-gallery-wrapper" onClick={e => e.stopPropagation()}>
            {renderCustomControls()}

            <ImageGallery
              ref={galleryRef}
              items={galleryItems}
              startIndex={currentIndex}
              showNav={true}
              showThumbnails={true}
              showFullscreenButton={false}
              showPlayButton={false}
              showBullets={false}
              renderItem={renderItem}
              onSlide={handleSlide}
              slideDuration={300}
              thumbnailPosition="bottom"
              additionalClass="corgee-image-gallery"
              disableSwipe={true} // Disable swipe to allow panning freely
            />

            {contextMenu && (
              <div
                className="ig-context-menu"
                style={{ top: contextMenu.y, left: contextMenu.x }}
                onClick={e => e.stopPropagation()}
              >
                <div className="ig-context-menu-item" onClick={handleContextMenuOpen}>
              Open image in a new window
                </div>
              </div>
            )}

            {/* Footer - Custom external to allow "Download All" and persistent caption */}
            <div className="ig-footer-external">
              <h4>{attachments[currentIndex]?.Description || 'Image'}</h4>
              <div className="ig-footer-links">
                <span role="button" tabIndex={0} onClick={handleDownload}>
              Download
                </span>
                {attachments.length > 1 && (
                  <span role="button" tabIndex={0} onClick={handleDownloadAll}>
                Download All
                  </span>
                )}
                <span role="button" tabIndex={0} onClick={copyLinkToClipboard}>
                  {linkCopied ? 'Link Copied!' : 'Copy Image Link To Clipboard'}
                </span>
              </div>
            </div>
          </div>
        </div>,
        document.body,
      )}

      {editorOpen && (
        <ImageFeedbackEditor
          imageSrc={editingImageUrl}
          onSave={handleSaveAnnotatedImage}
          onCancel={handleCancelEdit}
          onDownload={handleDownloadAnnotatedImage}
          hidden={editorHidden}
        />
      )}

      <SaveAnnotatedImageModal
        open={saveModalOpen}
        imageBase64={annotatedImageBase64}
        annotatedImageBase64={annotatedImageBase64}
        defaultCardId={attachments[currentIndex]?.ConversationId}
        defaultCardTitle={attachments[currentIndex]?.ConversationTitle}
        defaultProjectName={attachments[currentIndex]?.ProjectName}
        onClose={handleCloseSaveModal}
        onBack={handleBackToEditor}
        onReplyCreated={handleClose}
        onCreateReply={handleCreateReply}
      />
    </>
  );
};

export default ImagesGallery;
